import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axiosApi from '../../services/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { categoryOptions, isSystemDefinedField } from '../../utils/eventCategories';
import { validateEventForm, validateRegistrationFields } from '../../utils/eventFormValidation';
import { normalizeOption } from '../../utils/teamAgeValidation';

const EventForm = ({ event, isEdit = false, onSuccess, onError }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationQuery, setLocationQuery] = useState('');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const searchTimeoutRef = useRef(null);
  const [uispUsers, setUispUsers] = useState([]);
  const toLocalDateInputValue = (value) => {
    if (!value) {
      return '';
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const toDateInputValue = toLocalDateInputValue;
  const toLocalDateTimeInputValue = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [formData, setFormData] = useState({
    title: event?.title || '',
    description: event?.description || '',
    location: event?.location || '',
    eventDate: toDateInputValue(event?.eventDate || event?.event_date),
    eventEndDate: toDateInputValue(event?.eventEndDate || event?.event_end_date || event?.eventDate || event?.event_date),
    startTime: event?.startTime || event?.start_time || '',
    endTime: event?.endTime || event?.end_time || '',
    registrationDeadline: event?.registrationDeadline ?
      toLocalDateTimeInputValue(event?.registrationDeadline) : '',
    maxParticipants: event?.maxParticipants || event?.max_participants || '',
    category: event?.category || '',
    scope: event?.scope || 'territorial',
    price: event?.price || '',
    imageUrl: event?.imageUrl || event?.image_url || '',
    sda: event?.sda || '',
    image: null,
    documents: [],
    audioFiles: [], // Array per file audio
    existingDocuments: event?.documents || [],
    existingAudioFiles: event?.audioFiles || [], // File audio esistenti
    regulation: null, // Nuovo file regolamento
    existingRegulation: event?.documents?.find(doc => {
      const fileName = doc.name || doc.filename || '';
      return fileName.toLowerCase().includes('regolamento');
    }) || null,
    registrationFields: event?.registrationFields || [],
    registrationMode: event?.registrationMode || 'team', // 'team' = iscrizione squadre, 'individual' = seleziona soci individuali
    assignedTo: event?.assignedTo || '', // ID dell'utente UISP assegnato
    allowLateRegistration: event?.allowLateRegistration || false // Permette iscrizioni tardive per admin
  });

  // Spiegazione modalità iscrizione
  const registrationModeHelp = {
    team: "Iscrizione squadre: l'associazione iscrive una o più squadre/formazioni/gruppi. Ogni squadra può essere composta da più membri, e la registrazione avviene per gruppo.",
    individual: "Seleziona soci individuali: l'associazione iscrive i singoli soci partecipanti all'evento. Ogni partecipante viene selezionato individualmente dall'elenco soci."
  };



  // Per gli utenti SDA, imposta automaticamente categoria + campi default (solo se non già impostata)
  useEffect(() => {
    if (user && user.role && user.role.startsWith('sda_') && user.discipline && !isEdit && !formData.category) {
      console.log('SDA user detected in EventForm, setting default category to:', user.discipline);
      const selectedCategory = categoryOptions.find(cat => cat.value === user.discipline);
      setFormData(prev => ({
        ...prev,
        category: user.discipline,
        registrationFields: selectedCategory && prev.registrationFields.length === 0
          ? selectedCategory.defaultFields.map(field => ({
              ...field,
              options: field.options || []
            }))
          : prev.registrationFields,
        registrationMode: user.discipline === 'danza' ? 'team' : prev.registrationMode
      }));
    }
  }, [user, isEdit, formData.category]);

  // Effetto per caricare gli utenti UISP (solo per admin)
  useEffect(() => {
    const loadUispUsers = async () => {
      if (user && user.role === 'admin') {
        try {
          const response = await axiosApi.get('/admin/users/uisp');
          setUispUsers(response.data.users || []);
        } catch (error) {
          console.error('Errore nel caricamento degli utenti UISP:', error);
        }
      }
    };

    loadUispUsers();
  }, [user]);

  // Effetto per impostare il valore iniziale della location quando il componente si carica
  useEffect(() => {
    if (event?.location && !locationQuery) {
      setLocationQuery(event.location);
      setSelectedLocation({
        id: 'initial',
        display_name: event.location,
        lat: 0,
        lon: 0
      });
    }
  }, [event, locationQuery]);

  // Funzione per cercare indirizzi utilizzando OpenStreetMap Nominatim
  const searchLocations = async (query) => {
    if (!query || query.length < 3) {
      setLocationSuggestions([]);
      return;
    }

    setIsLoadingLocation(true);

    try {
      // Usiamo l'API di OpenStreetMap Nominatim per la ricerca degli indirizzi
      const response = await axios.get(`https://nominatim.openstreetmap.org/search`, {
        params: {
          q: query,
          format: 'json',
          addressdetails: 1,
          limit: 5
        },
        headers: {
          'Accept-Language': 'it' // Risultati in italiano
        }
      });

      const suggestions = response.data.map(item => ({
        id: item.place_id,
        display_name: item.display_name,
        lat: item.lat,
        lon: item.lon
      }));

      setLocationSuggestions(suggestions);
    } catch (error) {
      console.error('Errore nella ricerca degli indirizzi:', error);
    } finally {
      setIsLoadingLocation(false);
    }
  };

  // Gestisce il cambio del campo di ricerca indirizzo con debounce
  const handleLocationSearch = (e) => {
    const query = e.target.value;
    setLocationQuery(query);

    // Implementazione di debounce per limitare le richieste API
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchLocations(query);
    }, 500);
  };

  // Gestisce la selezione di un indirizzo dalla lista dei suggerimenti
  const handleSelectLocation = (location) => {
    setSelectedLocation(location);
    setLocationQuery(location.display_name);
    setFormData(prev => ({
      ...prev,
      location: location.display_name
    }));
    setLocationSuggestions([]);
  };

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target;

    if (type === 'file') {
      if (name === 'documents') {
        // Handle multiple document files
        setFormData(prev => ({
          ...prev,
          [name]: Array.from(files)
        }));
      } else if (name === 'regulation') {
        // Handle regulation file
        setFormData(prev => ({
          ...prev,
          regulation: files[0] || null
        }));
      } else {
        // Handle single file (image)
        setFormData(prev => ({
          ...prev,
          [name]: files[0]
        }));
      }
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      // Se cambia la data dell'evento, calcola automaticamente la deadline (3 giorni prima, ora locale)
      if (name === 'eventDate' && value) {
        const eventDate = new Date(value + 'T12:00:00');
        const deadline = new Date(eventDate);
        deadline.setDate(deadline.getDate() - 3);
        deadline.setHours(23, 59, 0, 0);
        const deadlineStr = toLocalDateTimeInputValue(deadline); // Formato datetime-local
        setFormData(prev => ({
          ...prev,
          [name]: value,
          eventEndDate: prev.eventEndDate && prev.eventEndDate < value ? value : (prev.eventEndDate || value),
          registrationDeadline: deadlineStr
        }));
        return;
      }

      if (name === 'eventEndDate' && value) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        return;
      }
      
      // Se la categoria viene cambiata, aggiorniamo campi + modalità in un unico update atomico
      if (name === 'category') {
        setFormData(prev => {
          let newRegistrationMode = prev.registrationMode;

          // Categorie che usano l'iscrizione a squadre
          if (value === 'danza' || value === 'calcio' || value === 'pallacanestro' || value === 'pallavolo') {
            newRegistrationMode = 'team';
          }
          // Categorie che usano l'iscrizione individuale
          else if (value === 'manifestazione' || value === 'atletica leggera' || value === 'ciclismo' || value === 'tennis') {
            newRegistrationMode = 'individual';
          }

          const selectedCategory = categoryOptions.find(cat => cat.value === value);
          if (selectedCategory) {
            const defaultFields = selectedCategory.defaultFields.map(field => {
              const fieldType = field.fieldType === 'audio'
                ? 'audio'
                : ['text', 'number', 'select', 'multiselect', 'file', 'audio'].includes(field.fieldType)
                  ? field.fieldType
                  : 'text';

              return {
                ...field,
                fieldType,
                options: field.options || []
              };
            });

            return {
              ...prev,
              [name]: value,
              registrationFields: [...defaultFields],
              registrationMode: newRegistrationMode
            };
          }
          return {
            ...prev,
            [name]: value,
            registrationMode: newRegistrationMode
          };
        });
        return;
      }

      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Gestisce l'aggiunta di un nuovo campo di iscrizione
  const handleAddField = () => {
    setFormData(prevData => ({
      ...prevData,
      registrationFields: [
        ...prevData.registrationFields,
        { fieldName: '', fieldLabel: '', fieldType: 'text', isRequired: false, options: [] }
      ]
    }));
  };

  // Gestisce la rimozione di un campo di iscrizione
  const handleRemoveField = (index) => {
    const updatedFields = [...formData.registrationFields];
    updatedFields.splice(index, 1);
    setFormData({
      ...formData,
      registrationFields: updatedFields
    });
  };

  // Gestisce la modifica di un campo di iscrizione
  const handleFieldChange = (index, field, value) => {
    const updatedFields = [...formData.registrationFields];

    // Se il campo è predefinito dal sistema, non permettiamo di modificarne alcune proprietà
    if (isSystemDefinedField(updatedFields[index].fieldName)) {
      if (['fieldName', 'fieldType'].includes(field)) {
        console.log('Tentativo di modificare un campo predefinito:', field);
        return; // Non permettiamo la modifica di queste proprietà per campi predefiniti
      }
    }

    updatedFields[index] = {
      ...updatedFields[index],
      [field]: value
    };

    // Se il tipo del campo è cambiato a 'select' o 'multiselect', inizializziamo le opzioni
    if (field === 'fieldType' && (value === 'select' || value === 'multiselect') && !updatedFields[index].options) {
      updatedFields[index].options = [''];
    }

    setFormData({
      ...formData,
      registrationFields: updatedFields
    });
  };

  // Gestisce l'aggiunta di una opzione per i campi select/multiselect
  const handleAddOption = (fieldIndex) => {
    const updatedFields = [...formData.registrationFields];
    if (!updatedFields[fieldIndex].options) {
      updatedFields[fieldIndex].options = [''];
    } else {
      updatedFields[fieldIndex].options.push('');
    }

    setFormData({
      ...formData,
      registrationFields: updatedFields
    });
  };

  // Gestisce la modifica di una opzione per i campi select/multiselect
  const handleOptionChange = (fieldIndex, optionIndex, value) => {
    const updatedFields = [...formData.registrationFields];
    const current = updatedFields[fieldIndex].options[optionIndex];
    if (current && typeof current === 'object') {
      updatedFields[fieldIndex].options[optionIndex] = {
        ...current,
        value,
        label: value
      };
    } else {
      updatedFields[fieldIndex].options[optionIndex] = value;
    }

    setFormData({
      ...formData,
      registrationFields: updatedFields
    });
  };

  const handleOptionAgeMetaChange = (fieldIndex, optionIndex, key, rawValue) => {
    const updatedFields = [...formData.registrationFields];
    const current = updatedFields[fieldIndex].options[optionIndex];
    const label = typeof current === 'string'
      ? current
      : (current?.label || current?.value || '');
    const parsed = rawValue === '' ? null : Number(rawValue);

    updatedFields[fieldIndex].options[optionIndex] = {
      ...normalizeOption(current || label),
      label,
      value: label,
      [key]: parsed
    };

    setFormData({
      ...formData,
      registrationFields: updatedFields
    });
  };

  const showAgeRulesForField = (field) => {
    return formData.category === 'danza'
      && (field.enforceAgeRules || field.fieldName === 'ageBand')
      && (field.fieldType === 'select' || field.fieldType === 'multiselect');
  };

  // Gestisce la rimozione di una opzione per i campi select/multiselect
  const handleRemoveOption = (fieldIndex, optionIndex) => {
    const updatedFields = [...formData.registrationFields];
    updatedFields[fieldIndex].options.splice(optionIndex, 1);

    setFormData({
      ...formData,
      registrationFields: updatedFields
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validazione centralizzata prima dell'invio
    const formCheck = validateEventForm(formData);
    if (!formCheck.isValid) {
      setError(formCheck.errors[0]);
      if (onError && typeof onError === 'function') onError(formCheck.errors[0]);
      return;
    }
    if (formData.registrationFields && formData.registrationFields.length > 0) {
      const fieldsCheck = validateRegistrationFields(formData.registrationFields);
      if (!fieldsCheck.isValid) {
        setError(fieldsCheck.errors[0]);
        if (onError && typeof onError === 'function') onError(fieldsCheck.errors[0]);
        return;
      }
    }

    setLoading(true);

    console.log(`Submitting event form in ${isEdit ? 'edit' : 'create'} mode`);

    // Prepara elenco documenti esistenti da mantenere
    const existingDocumentsPayload = Array.isArray(formData.existingDocuments)
      ? formData.existingDocuments.map(doc => ({
          _id: doc._id || doc.id || doc.path,   // Prisma docs use `path` as identifier
          name: doc.name || doc.filename,
          path: doc.path,
          contentType: doc.contentType || doc.mimetype
        })).filter(doc => doc._id)
      : [];

    // Check if any files need to be uploaded
    const hasFiles = (formData.image instanceof File) ||
      (formData.regulation instanceof File) ||
      (Array.isArray(formData.documents) && formData.documents.some(d => d instanceof File)) ||
      (Array.isArray(formData.audioFiles) && formData.audioFiles.some(a => a instanceof File)) ||
      (Array.isArray(formData.registrationFields) && formData.registrationFields.some(f => f.file instanceof File));

    let dataToSubmit;

    if (hasFiles) {
      // Use FormData for multipart/form-data (file uploads)
      const apiFormData = new FormData();

      const appendIfDefined = (key, value) => {
        if (value !== undefined && value !== null) {
          apiFormData.append(key, value);
        }
      };

      appendIfDefined('title', formData.title);
      appendIfDefined('description', formData.description);
      appendIfDefined('location', formData.location);
      appendIfDefined('eventDate', formData.eventDate);
      appendIfDefined('eventEndDate', formData.eventEndDate || formData.eventDate);
      appendIfDefined('startTime', formData.startTime);
      appendIfDefined('endTime', formData.endTime);
      appendIfDefined('registrationDeadline', formData.registrationDeadline);
      appendIfDefined('maxParticipants', formData.maxParticipants);
      appendIfDefined('category', formData.category);
      appendIfDefined('scope', formData.scope || 'territorial');
      appendIfDefined('price', formData.price);
      appendIfDefined('sda', formData.sda);
      appendIfDefined('registrationMode', formData.registrationMode);
      appendIfDefined('isPublic', formData.isPublic);
      appendIfDefined('allowLateRegistration', formData.allowLateRegistration);

      if (formData.assignedTo) {
        appendIfDefined('assignedTo', formData.assignedTo);
      }

      if (formData.registrationFields && formData.registrationFields.length > 0) {
        appendIfDefined('registrationFields', JSON.stringify(formData.registrationFields));
      }

      if (isEdit) {
        appendIfDefined('existingDocuments', JSON.stringify(existingDocumentsPayload));
      }

      if (formData.image instanceof File) {
        apiFormData.append('image', formData.image);
      }

      if (formData.regulation instanceof File) {
        const regulationName = formData.regulation.name?.startsWith('Regolamento_')
          ? formData.regulation.name
          : `Regolamento_${formData.regulation.name}`;
        apiFormData.append('documents', formData.regulation, regulationName);
      }

      if (formData.documents && formData.documents.length > 0) {
        formData.documents.forEach(doc => {
          apiFormData.append('documents', doc);
        });
      }

      if (formData.audioFiles && formData.audioFiles.length > 0) {
        formData.audioFiles.forEach(audio => {
          apiFormData.append('audioFiles', audio);
        });
      }

      formData.registrationFields.forEach((field, index) => {
        if (field.file) {
          const fieldType = field.fieldType === 'audio' ? 'customFieldAudio' : 'customFieldFile';
          apiFormData.append(`${fieldType}_${index}`, field.file);
          apiFormData.append(`${fieldType}_${index}_metadata`, JSON.stringify({
            fieldName: field.fieldName,
            fieldLabel: field.fieldLabel,
            fieldType: field.fieldType,
            isRequired: field.isRequired
          }));
        }
      });

      dataToSubmit = apiFormData;
    } else {
      // No files to upload - use JSON body (avoids multipart proxy issues with Apache)
      const jsonData = {};
      const addIfDefined = (key, value) => {
        if (value !== undefined && value !== null) jsonData[key] = value;
      };

      addIfDefined('title', formData.title);
      addIfDefined('description', formData.description);
      addIfDefined('location', formData.location);
      addIfDefined('eventDate', formData.eventDate);
      addIfDefined('eventEndDate', formData.eventEndDate || formData.eventDate);
      addIfDefined('startTime', formData.startTime);
      addIfDefined('endTime', formData.endTime);
      addIfDefined('registrationDeadline', formData.registrationDeadline);
      addIfDefined('maxParticipants', formData.maxParticipants);
      addIfDefined('category', formData.category);
      addIfDefined('scope', formData.scope || 'territorial');
      addIfDefined('price', formData.price);
      addIfDefined('sda', formData.sda);
      addIfDefined('registrationMode', formData.registrationMode);
      addIfDefined('isPublic', formData.isPublic);
      addIfDefined('allowLateRegistration', formData.allowLateRegistration);
      if (formData.assignedTo) addIfDefined('assignedTo', formData.assignedTo);
      if (formData.registrationFields && formData.registrationFields.length > 0) {
        jsonData.registrationFields = formData.registrationFields;
      }
      if (isEdit) {
        jsonData.existingDocuments = existingDocumentsPayload;
      }

      dataToSubmit = jsonData;
    }

    console.log('Processing form data for API submission');
    console.log('Form data to submit:', isEdit ? 'UPDATE' : 'CREATE', dataToSubmit);

    try {
      const method = isEdit ? 'put' : 'post';
      // Handle ID
      const eventId = event?.id;
      const endpoint = isEdit ? `/api/events/${eventId}` : '/api/events';
      console.log(`Using API endpoint: ${endpoint} with method: ${method}`);

      const response = await axiosApi[method](endpoint, dataToSubmit);
      console.log('API response:', response.data);

      setSuccess(true);

      // If external onSuccess callback is provided, call it
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(response.data);
      } else {
        // Default behavior - redirect after delay
        setTimeout(() => {
          if (user.role === 'uisp_territorial') {
            navigate('/territorial/dashboard');
          } else if (user.role === 'uisp_regional') {
            navigate('/regional/dashboard');
          } else if (user.role === 'uisp_national') {
            navigate('/national/dashboard');
          } else {
            navigate('/admin/events');
          }
        }, 1500);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to save event';
      setError(errorMessage);
      console.error('Error during event submission:', err);

      // If external onError callback is provided, call it
      if (onError && typeof onError === 'function') {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <Card className="shadow">
      <Card.Header className="bg-primary text-white">
        <h4>{isEdit ? 'Edit Event' : 'Create New Event'}</h4>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">Event successfully {isEdit ? 'updated' : 'created'}!</Alert>}

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Event Title*</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Location*</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    name="locationSearch"
                    value={locationQuery}
                    onChange={handleLocationSearch}
                    placeholder="Cerca indirizzo..."
                    autoComplete="off"
                    required
                  />
                  {isLoadingLocation && (
                    <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                      <Spinner animation="border" size="sm" />
                    </div>
                  )}

                  {locationSuggestions.length > 0 && (
                    <div
                      className="position-absolute w-100 mt-1 shadow-sm border rounded bg-white z-3"
                      style={{ zIndex: 1000 }}
                    >
                      <ul className="list-group list-group-flush">
                        {locationSuggestions.map(location => (
                          <li
                            key={location.id}
                            className="list-group-item list-group-item-action py-2 px-3"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSelectLocation(location)}
                          >
                            <small>{location.display_name}</small>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Campo nascosto per mantenere la compatibilità con la validazione del form */}
                <Form.Control
                  type="hidden"
                  name="location"
                  value={formData.location}
                  required
                />

                {selectedLocation && (
                  <div className="mt-2">
                    <span className="badge bg-success">Indirizzo selezionato: {selectedLocation.display_name}</span>
                    {selectedLocation.id !== 'initial' && (
                      <div className="mt-1">
                        <small className="text-muted">
                          Coordinate: {selectedLocation.lat}, {selectedLocation.lon}
                        </small>
                      </div>
                    )}
                  </div>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Data inizio evento</Form.Label>
                <Form.Control
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Data fine evento</Form.Label>
                <Form.Control
                  type="date"
                  name="eventEndDate"
                  value={formData.eventEndDate}
                  onChange={handleChange}
                  min={formData.eventDate || undefined}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <div className="mb-3">
                  <label className="form-label">Categoria</label>
                  <select
                    className="form-select"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Seleziona una categoria</option>
                    {user && user.role && user.role.startsWith('sda_') ? (
                      // Per utenti SDA: mostra solo la loro disciplina, manifestazione e stage
                      <>
                        {user.discipline && (
                          <option value={user.discipline}>
                            {categoryOptions.find(opt => opt.value === user.discipline)?.label || user.discipline.charAt(0).toUpperCase() + user.discipline.slice(1)}
                          </option>
                        )}
                        <option value="manifestazione">Manifestazione</option>
                        <option value="stage">Stage</option>
                      </>
                    ) : (
                      // Per altri utenti: mostra tutte le categorie
                      <>
                        {categoryOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                        <option value="acquaviva">Acquaviva</option>
                        <option value="atletica leggera">Atletica Leggera</option>
                        <option value="calcio">Calcio</option>
                        <option value="ciclismo">Ciclismo</option>
                        <option value="discipline orientali">Discipline Orientali</option>
                        <option value="equestri e cinofile">Equestri e Cinofile</option>
                        <option value="ginnastiche">Ginnastiche</option>
                        <option value="giochi">Giochi</option>
                        <option value="montagna">Montagna</option>
                        <option value="motorismo">Motorismo</option>
                        <option value="neve">Neve</option>
                        <option value="nuoto">Nuoto</option>
                        <option value="pallacanestro">Pallacanestro</option>
                        <option value="pallavolo">Pallavolo</option>
                        <option value="pattinaggio">Pattinaggio</option>
                        <option value="subacquea">Subacquea</option>
                        <option value="tennis">Tennis</option>
                        <option value="vela">Vela</option>
                        <option value="multi-sport">Multi-Sport</option>
                        <option value="manifestazione">Manifestazione</option>
                        <option value="stage">Stage</option>
                      </>
                    )}
                  </select>
                  {user && user.role && user.role.startsWith('sda_') && (
                    <Form.Text className="text-info">
                      <i className="bi bi-info-circle me-1"></i>
                      Come responsabile SDA, puoi creare/modificare eventi nella categoria "{user.discipline}", "Manifestazione" o "Stage".
                    </Form.Text>
                  )}
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Livello evento *</Form.Label>
                <Form.Select
                  name="scope"
                  value={formData.scope || 'territorial'}
                  onChange={handleChange}
                  required
                >
                  <option value="territorial">Territoriale</option>
                  <option value="regional">Regionale</option>
                  <option value="national">Nazionale</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Il livello viene mostrato alle associazioni nel dettaglio evento.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>SDA / Disciplina (opzionale)</Form.Label>
                <Form.Control
                  type="text"
                  name="sda"
                  value={formData.sda || ''}
                  onChange={handleChange}
                  placeholder="Es. danza, judo..."
                  maxLength={100}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Campo Assegnazione - Visibile solo per admin */}
          {user && user.role === 'admin' && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Assegna evento a</Form.Label>
                  <Form.Select
                    name="assignedTo"
                    value={formData.assignedTo || ''}
                    onChange={handleChange}
                  >
                    <option value="">Nessuna assegnazione (pubblico)</option>
                    {uispUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.username} - {user.email || 'email non disponibile'} ({
                          user.role === 'uisp_national'
                            ? 'UISP Nazionale'
                            : user.role === 'uisp_regional'
                              ? 'UISP Regionale'
                              : 'UISP Territoriale'
                        })
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Seleziona l'utente UISP che gestirà questo evento
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Modalità iscrizione: team o individuale */}
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>
                  Modalità iscrizione <span style={{ color: 'red' }}>*</span>
                </Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    id="registrationMode-team"
                    name="registrationMode"
                    value="team"
                    checked={formData.registrationMode === 'team'}
                    onChange={handleChange}
                    label="Iscrizione squadre"
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="registrationMode-individual"
                    name="registrationMode"
                    value="individual"
                    checked={formData.registrationMode === 'individual'}
                    onChange={handleChange}
                    label="Seleziona soci individuali"
                  />
                </div>
                <div className="mt-2">
                  <small className="text-muted">
                    {registrationModeHelp[formData.registrationMode]}
                  </small>
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Start Time</Form.Label>
                <Form.Control
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>End Time</Form.Label>
                <Form.Control
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Chiusura iscrizioni *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="registrationDeadline"
                  value={formData.registrationDeadline}
                  onChange={handleChange}
                  required
                />
                <Form.Text className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Data e ora di chiusura delle iscrizioni. Impostata automaticamente 3 giorni prima dell'evento, ma può essere modificata.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Allow late registration - visible only for admin/privileged users */}
          {event && (
            <Row>
              <Col md={12}>
                <div className="alert alert-warning mb-3">
                  <Form.Check
                    type="switch"
                    id="allowLateRegistration"
                    name="allowLateRegistration"
                    label={
                      <span>
                        <i className="bi bi-unlock-fill me-2"></i>
                        <strong>Abilita iscrizioni tardive</strong>
                        <br />
                        <small className="text-muted">
                          Permette agli utenti admin/comitato di iscrivere partecipanti anche dopo la deadline. 
                          Le associazioni normali non potranno comunque iscrivere dopo la scadenza.
                        </small>
                      </span>
                    }
                    checked={formData.allowLateRegistration || false}
                    onChange={handleChange}
                  />
                </div>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Max Participants</Form.Label>
                <Form.Control
                  type="number"
                  name="maxParticipants"
                  value={formData.maxParticipants}
                  onChange={handleChange}
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Price (€)</Form.Label>
                <Form.Control
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="mt-4 mb-4">
            <h4 className="mb-3">Caricamento file</h4>

            <Row>
              <Col md={6}>
                <Card className="h-100 shadow-sm">
                  <Card.Header className="bg-primary text-white">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-image me-2 fs-5"></i>
                      <span>Immagine evento</span>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Control
                        type="file"
                        name="image"
                        onChange={handleChange}
                        accept="image/*"
                        className="form-control-sm"
                      />
                      <Form.Text className="text-muted">
                        Opzionale: Carica un'immagine per il tuo evento
                      </Form.Text>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="h-100 shadow-sm">
                  <Card.Header className="bg-warning text-dark">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-file-earmark-ruled me-2 fs-5"></i>
                      <span>Regolamento Evento</span>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Carica regolamento
                        {formData.existingRegulation && <span className="badge bg-success ms-2">Presente</span>}
                      </Form.Label>
                      <Form.Control
                        type="file"
                        name="regulation"
                        onChange={handleChange}
                        accept=".pdf,.doc,.docx"
                        className="form-control-sm"
                      />
                      <Form.Text className="text-muted">
                        Opzionale: Il regolamento deve essere accettato dalle associazioni prima dell'iscrizione.
                      </Form.Text>
                    </Form.Group>
                    
                    {formData.existingRegulation && (
                      <Alert variant="info" className="mb-0 py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <i className="bi bi-file-earmark-check me-2"></i>
                            <small>{formData.existingRegulation.name}</small>
                          </div>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => {
                              setFormData(prev => ({ 
                                ...prev, 
                                existingRegulation: null,
                                existingDocuments: prev.existingDocuments.filter(doc => {
                                  const docId = doc._id || doc.id || doc.path;
                                  const regId = formData.existingRegulation._id || formData.existingRegulation.id || formData.existingRegulation.path;
                                  return docId !== regId;
                                })
                              }));
                            }}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </Alert>
                    )}
                    
                    {formData.regulation && (
                      <Alert variant="success" className="mb-0 py-2 mt-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <i className="bi bi-file-earmark-plus me-2"></i>
                            <small>Nuovo: {formData.regulation.name}</small>
                          </div>
                          <span className="badge bg-success">
                            {(formData.regulation.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="h-100 shadow-sm">
                  <Card.Header className="bg-info text-white">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-file-earmark-text me-2 fs-5"></i>
                      <span>Documenti</span>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Control
                        type="file"
                        name="documents"
                        onChange={handleChange}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                        multiple
                        className="form-control-sm"
                      />
                      <Form.Text className="text-muted">
                        Opzionale: Carica documenti aggiuntivi (PDF, Word, Excel, ecc.). Max 5 file.
                      </Form.Text>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <div className="mt-3 alert alert-info">
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-music-note-beamed me-2 fs-5"></i>
                <strong>Gestione file audio</strong>
              </div>
              <p>
                Per richiedere il caricamento di file audio/musica alle associazioni, aggiungi un campo personalizzato di tipo "audio" nella sezione "Campi di iscrizione personalizzati" qui sotto.
              </p>
              <p>
                Le associazioni potranno caricare file audio durante la registrazione all'evento, ad esempio per coreografie, esibizioni, o altri elementi sonori necessari.
              </p>
              <p className="mb-0">
                <small>
                  <i className="bi bi-info-circle me-1"></i>
                  Esempi di campi audio: "Musica per esibizione", "Traccia audio per coreografia", "Accompagnamento musicale", ecc.
                </small>
              </p>
            </div>
          </div>

          {/* Display uploaded and existing files */}
          {(formData.documents?.length > 0 || (isEdit && formData.existingDocuments?.length > 0)) && (
            <div className="mt-2 mb-4">
              <h5 className="mb-3">File caricati</h5>
              <Row>
                <Col md={12}>
                  <Card className="shadow-sm">
                    <Card.Header className="bg-info text-white d-flex align-items-center">
                      <i className="bi bi-file-earmark-text me-2 fs-5"></i>
                      <span>Documenti</span>
                    </Card.Header>
                    <Card.Body style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {formData.documents && formData.documents.length > 0 && (
                        <div className="mb-3">
                          <h6>Nuovi documenti</h6>
                          <ul className="list-group">
                            {Array.from(formData.documents).map((file, index) => (
                              <li className="list-group-item d-flex justify-content-between align-items-center" key={index}>
                                <span>
                                  <i className="bi bi-file-earmark me-2"></i>
                                  {file.name}
                                </span>
                                <span className="badge bg-info text-white">{(file.size / 1024).toFixed(1)} KB</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {isEdit && formData.existingDocuments && formData.existingDocuments.length > 0 && (
                        <div>
                          <h6>Documenti esistenti</h6>
                          <ul className="list-group">
                            {formData.existingDocuments.map((doc, index) => (
                              <li className="list-group-item d-flex justify-content-between align-items-center" key={index}>
                                <span>
                                  <i className="bi bi-file-earmark me-2"></i>
                                  {doc.name}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => {
                                    const updatedDocs = [...formData.existingDocuments];
                                    updatedDocs.splice(index, 1);
                                    setFormData(prev => ({ ...prev, existingDocuments: updatedDocs }));
                                  }}
                                >
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}

          {/* Sezione per i campi di iscrizione personalizzati */}
          <Row className="mt-4">
            <Col>
              <Card className="border-0 bg-light">
                <Card.Header className="bg-light border-0">
                  <h4 className="mb-0">
                    <i className="bi bi-list-check me-2"></i>
                    Campi di iscrizione personalizzati
                  </h4>
                  <p className="text-muted small mb-0">
                    Configura quali informazioni dovranno essere inserite durante l'iscrizione all'evento.
                  </p>
                </Card.Header>
                <Card.Body>
                  {formData.registrationFields.length > 0 ? (
                    <div className="mb-3">
                      {formData.registrationFields.map((field, index) => (
                        <Card key={index} className="mb-3">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h5 className="card-title mb-0">Campo #{index + 1}</h5>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveField(index)}
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            </div>

                            <Row className="mb-2">
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label>Nome campo (identificativo)</Form.Label>
                                  {isSystemDefinedField(field.fieldName) ? (
                                    <div>
                                      <Form.Control
                                        type="text"
                                        value={field.fieldName}
                                        disabled
                                        className="bg-light"
                                      />
                                      <Form.Text className="text-muted">
                                        Campo identificativo predefinito, non modificabile
                                      </Form.Text>
                                    </div>
                                  ) : (
                                    <Form.Control
                                      type="text"
                                      value={field.fieldName}
                                      onChange={(e) => handleFieldChange(index, 'fieldName', e.target.value)}
                                      required
                                    />
                                  )}
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label>Etichetta (visibile all'utente)</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={field.fieldLabel}
                                    onChange={(e) => handleFieldChange(index, 'fieldLabel', e.target.value)}
                                    required
                                    disabled={isSystemDefinedField(field.fieldName)}
                                    className={isSystemDefinedField(field.fieldName) ? "bg-light" : ""}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>

                            <Row className="mb-2">
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label>Tipo campo</Form.Label>
                                  {isSystemDefinedField(field.fieldName) ? (
                                    <div>
                                      <Form.Control
                                        type="text"
                                        value={field.fieldType === 'audio' ? 'Audio/Musica' :
                                          field.fieldType === 'file' ? 'File Documento' :
                                            field.fieldType === 'select' ? 'Selezione singola' :
                                              field.fieldType === 'multiselect' ? 'Selezione multipla' :
                                                field.fieldType === 'number' ? 'Numero' : 'Testo'}
                                        disabled
                                        className="bg-light"
                                      />
                                      <Form.Text className="text-muted">
                                        Tipo campo predefinito, non modificabile
                                      </Form.Text>
                                    </div>
                                  ) : (
                                    <Form.Select
                                      value={field.fieldType}
                                      onChange={(e) => handleFieldChange(index, 'fieldType', e.target.value)}
                                    >
                                      <option value="text">Testo</option>
                                      <option value="number">Numero</option>
                                      <option value="select">Selezione singola</option>
                                      <option value="multiselect">Selezione multipla</option>
                                      <option value="file">File Documento</option>
                                      <option value="audio">Audio/Musica (per partecipanti)</option>
                                    </Form.Select>
                                  )}
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mt-4">
                                  <Form.Check
                                    type="checkbox"
                                    id={`required-${index}`}
                                    label="Campo obbligatorio"
                                    checked={field.isRequired}
                                    onChange={(e) => handleFieldChange(index, 'isRequired', e.target.checked)}
                                    disabled={isSystemDefinedField(field.fieldName)}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>

                            {/* Opzioni per i campi select/multiselect */}
                            {(field.fieldType === 'select' || field.fieldType === 'multiselect') && (
                              <div className="mt-3">
                                <Form.Label>Opzioni</Form.Label>
                                {showAgeRulesForField(field) && (
                                  <Form.Text className="d-block mb-2 text-muted">
                                    Per le categorie con età nel nome (es. &quot;Predanza 3 (8-10 anni)&quot;) puoi impostare
                                    età min/max e il numero massimo di partecipanti fuori fascia (default 2).
                                  </Form.Text>
                                )}
                                {field.options && field.options.map((option, optionIndex) => {
                                  const normalized = normalizeOption(option);
                                  const optionLabel = typeof option === 'string' ? option : (option?.label || option?.value || '');
                                  return (
                                  <div key={optionIndex} className="mb-3 border rounded p-2">
                                    <div className="input-group mb-2">
                                      <Form.Control
                                        type="text"
                                        value={optionLabel}
                                        onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                                        placeholder={`Opzione ${optionIndex + 1}`}
                                        required
                                      />
                                      <Button
                                        variant="outline-danger"
                                        onClick={() => handleRemoveOption(index, optionIndex)}
                                      >
                                        <i className="bi bi-x"></i>
                                      </Button>
                                    </div>
                                    {showAgeRulesForField(field) && (
                                      <Row className="g-2">
                                        <Col xs={4}>
                                          <Form.Label className="small mb-0">Età min</Form.Label>
                                          <Form.Control
                                            type="number"
                                            min={0}
                                            max={120}
                                            value={normalized.minAge ?? ''}
                                            onChange={(e) => handleOptionAgeMetaChange(index, optionIndex, 'minAge', e.target.value)}
                                            placeholder="es. 8"
                                          />
                                        </Col>
                                        <Col xs={4}>
                                          <Form.Label className="small mb-0">Età max</Form.Label>
                                          <Form.Control
                                            type="number"
                                            min={0}
                                            max={120}
                                            value={normalized.maxAge ?? ''}
                                            onChange={(e) => handleOptionAgeMetaChange(index, optionIndex, 'maxAge', e.target.value)}
                                            placeholder="es. 10"
                                          />
                                        </Col>
                                        <Col xs={4}>
                                          <Form.Label className="small mb-0">Max fuori fascia</Form.Label>
                                          <Form.Control
                                            type="number"
                                            min={0}
                                            max={20}
                                            value={normalized.maxOutOfQuota ?? ''}
                                            onChange={(e) => handleOptionAgeMetaChange(index, optionIndex, 'maxOutOfQuota', e.target.value)}
                                            placeholder="2"
                                          />
                                        </Col>
                                      </Row>
                                    )}
                                  </div>
                                  );
                                })}
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => handleAddOption(index)}
                                >
                                  <i className="bi bi-plus-circle me-1"></i>
                                  Aggiungi opzione
                                </Button>
                              </div>
                            )}

                            {/* Input per file/audio */}
                            {(field.fieldType === 'file' || field.fieldType === 'audio') && (
                              <div className="mt-3">
                                <div className={field.fieldType === 'audio' ? "border border-primary rounded p-3" : "border rounded p-3"}>
                                  <div className={field.fieldType === 'audio' ? "bg-primary text-white p-2 mb-3 rounded" : "bg-light p-2 mb-3 rounded"}>
                                    <div className="d-flex align-items-center">
                                      {field.fieldType === 'audio' ? (
                                        <>
                                          <i className="bi bi-music-note-beamed me-2"></i>
                                          <span><strong>File Audio/Musica</strong></span>
                                        </>
                                      ) : (
                                        <>
                                          <i className="bi bi-file-earmark me-2"></i>
                                          <span><strong>File Documento</strong></span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <Form.Label>
                                    {field.fieldType === 'audio'
                                      ? 'Esempio file audio/musica'
                                      : 'Esempio documento'}
                                  </Form.Label>
                                  <Form.Control
                                    type="file"
                                    accept={field.fieldType === 'audio'
                                      ? 'audio/mp3,audio/wav,audio/ogg,audio/mpeg'
                                      : '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt'}
                                    onChange={(e) => handleFieldChange(index, 'file', e.target.files[0])}
                                  />
                                  {field.fieldType === 'audio' && (
                                    <div className="mt-2 alert alert-info py-2">
                                      <i className="bi bi-info-circle me-1"></i>
                                      <span>Questo campo permetterà ai partecipanti di caricare un file audio per questo evento.</span>
                                      <div><small>Formati supportati: MP3, WAV, OGG, MPEG.</small></div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Alert variant="info">
                      <i className="bi bi-info-circle me-2"></i>
                      Non ci sono campi di iscrizione personalizzati. Aggiungi dei campi o seleziona una categoria predefinita.
                    </Alert>
                  )}

                  <Button
                    variant="outline-success"
                    onClick={handleAddField}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Aggiungi campo personalizzato
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="d-flex justify-content-between mt-4">
            <Button
              variant="secondary"
              onClick={() => window.history.back()}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? 'Saving...' : isEdit ? 'Update Event' : 'Create Event'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default EventForm;
